/**
 * Guesty Booking Engine API Client
 *
 * Service for interacting with Guesty Booking Engine API endpoints
 * through our backend proxy.
 *
 * Flow:
 * 1. Create quote → get pricing from Guesty
 * 2. Create hold with quoteId → reserve dates
 * 3. Create payment → confirm booking
 *
 * @module guesty-booking
 */

// ============================================================================
// Types
// ============================================================================

export interface CreateQuoteParams {
  listingId: string;
  checkIn: string;      // YYYY-MM-DD
  checkOut: string;     // YYYY-MM-DD
  guests: number;
  coupon?: string;
}

export interface QuoteResponse {
  quoteId: string;
  ratePlanId: string;
  expiresAt: string;    // ISO 8601 timestamp
  pricing: {
    accommodation: number;
    cleaning: number;
    fees: number;       // Additional fees (totalFees - cleaning)
    taxes: number;
    total: number;
    currency: string;
  };
  nights: number;
  pricePerNight: number;
  /** Raw Guesty response (for debugging and instant booking) */
  raw?: unknown;
}

export interface CreateHoldWithQuoteParams {
  unitId: string;
  checkIn: string;      // YYYY-MM-DD
  checkOut: string;     // YYYY-MM-DD
  guests: number;
  sessionId: string;
  quoteId: string;
  ratePlanId: string;
}

export interface HoldResponse {
  holdId: string;
  expiresAt: string;    // ISO 8601 timestamp
}

export interface CreateBookingParams {
  quoteId: string;
  ratePlanId: string;
  paymentMethodId: string;  // pm_xxxxx from Stripe
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
}

export interface BookingResponse {
  success: boolean;
  reservationId?: string;
  confirmationCode?: string;  // GY-XXXXXXXX
  error?: string;
}

// ============================================================================
// API Helpers
// ============================================================================

/**
 * Get the API base URL
 * Uses NEXT_PUBLIC_API_URL in development/production, falls back to relative URL
 */
function getApiUrl(endpoint: string): string {
  const apiBase = process.env.NEXT_PUBLIC_API_URL;
  if (apiBase) {
    return `${apiBase}${endpoint}`;
  }
  return endpoint;
}

// ============================================================================
// API Client Functions
// ============================================================================

/**
 * Create a quote to get real-time pricing from Guesty Booking Engine API
 *
 * Uses the Cloud Functions endpoint /api/guesty/quotes that:
 * 1. Gets token automatically from Firestore
 * 2. Calls Guesty BE API
 * 3. Parses response with correct structure (rates.ratePlans[0].ratePlan.money)
 *
 * @param params Quote parameters
 * @returns Quote with pricing and expiration
 * @throws Error if quote creation fails
 */
export async function createQuote(params: CreateQuoteParams): Promise<QuoteResponse> {
  const response = await fetch(getApiUrl('/api/guesty/quotes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      listingId: params.listingId,
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      guests: params.guests,
      ...(params.coupon && { coupons: params.coupon }),
    }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    const errorMessage = data.error?.message || data.error || `Quote creation failed: ${response.status}`;
    throw new Error(errorMessage);
  }

  // Map Cloud Functions API response to QuoteResponse interface
  // The backend returns: { success: true, data: QuoteSummary }
  const quoteData = data.data;

  return {
    quoteId: quoteData.quoteId,
    ratePlanId: quoteData.ratePlanId,
    expiresAt: quoteData.expiresAt,
    pricing: {
      accommodation: quoteData.pricing.accommodation,
      cleaning: quoteData.pricing.cleaning,
      fees: quoteData.pricing.fees || 0,
      taxes: quoteData.pricing.taxes,
      total: quoteData.pricing.total,
      currency: quoteData.pricing.currency,
    },
    nights: quoteData.dates?.nights || 0,
    pricePerNight: quoteData.pricePerNight || 0,
    raw: quoteData.raw,
  };
}

/**
 * Get an existing quote by ID
 *
 * Uses the Cloud Functions endpoint /api/guesty/quotes/:quoteId
 *
 * @param quoteId The quote ID to retrieve
 * @returns Quote data if found and not expired
 * @throws Error if quote not found or expired
 */
export async function getQuote(quoteId: string): Promise<QuoteResponse> {
  const response = await fetch(getApiUrl(`/api/guesty/quotes/${encodeURIComponent(quoteId)}`));

  const data = await response.json();

  if (!response.ok || !data.success) {
    const errorCode = data.error?.code;
    if (errorCode === 'QUOTE_EXPIRED') {
      throw new Error('Quote expired');
    }
    const errorMessage = data.error?.message || data.error || `Failed to get quote: ${response.status}`;
    throw new Error(errorMessage);
  }

  // Map Cloud Functions API response to QuoteResponse interface
  // The backend returns: { success: true, data: QuoteSummary }
  const quoteData = data.data;

  return {
    quoteId: quoteData.quoteId,
    ratePlanId: quoteData.ratePlanId,
    expiresAt: quoteData.expiresAt,
    pricing: {
      accommodation: quoteData.pricing.accommodation,
      cleaning: quoteData.pricing.cleaning,
      fees: quoteData.pricing.fees || 0,
      taxes: quoteData.pricing.taxes,
      total: quoteData.pricing.total,
      currency: quoteData.pricing.currency,
    },
    nights: quoteData.dates?.nights || 0,
    pricePerNight: quoteData.pricePerNight || 0,
    raw: quoteData.raw,
  };
}

/**
 * Check if a quote has expired
 *
 * @param expiresAt ISO 8601 timestamp
 * @returns true if expired, false otherwise
 */
export function isQuoteExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

/**
 * Get minutes remaining until quote expires
 *
 * @param expiresAt ISO 8601 timestamp
 * @returns Minutes until expiry (0 if expired)
 */
export function getMinutesUntilExpiry(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(diff / 60000));
}

/**
 * Create a hold with quote data
 *
 * This replaces the standard POST /api/holds when using Guesty BE API.
 * The hold will be created with frozen pricing from the quote.
 *
 * @param params Hold creation parameters including quoteId
 * @returns Hold ID and expiration time
 * @throws Error if hold creation fails
 */
export async function createHoldWithQuote(
  params: CreateHoldWithQuoteParams
): Promise<HoldResponse> {
  const response = await fetch(getApiUrl('/api/holds'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create hold' }));
    throw new Error(error.message || `Hold creation failed: ${response.status}`);
  }

  const data = await response.json();

  // Handle both new and legacy response formats
  if (data.success && data.data) {
    return {
      holdId: data.data.id,
      expiresAt: data.data.expiresAt,
    };
  }

  return {
    holdId: data.holdId || data.id,
    expiresAt: data.expiresAt,
  };
}

/**
 * Format price for display
 *
 * @param amount Price amount
 * @param currency Currency code
 * @returns Formatted price string
 */
export function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format date range for display
 *
 * @param checkIn YYYY-MM-DD
 * @param checkOut YYYY-MM-DD
 * @returns Formatted date range
 */
export function formatDateRange(checkIn: string, checkOut: string): string {
  const startDate = new Date(checkIn);
  const endDate = new Date(checkOut);

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
}
